import React, { useState, useEffect, useRef } from "react";
import {
  reportError,
  reportSpam,
  SelectInputWithImg,
  ModalConfirmReportMessage,
  ModalConfirmSpam,
  trashGalleryIcon,
  GalleryVideo,
  Toast,
  downloadImgInChat,
  redirecttoPage,
  documentIcon,
} from "../../../Components";
import { useLocation, useNavigate } from "react-router-dom";
import { chatService, uploadService } from "../../../Services";
import { socketAuthService } from "../../../Services/socket.service";
import { ChevronDown, CircleAlert, Paperclip, Send } from "lucide-react";
import { parseInput } from "../../Constants/AppConstant";
import Tooltip from "@mui/material/Tooltip";
import socketService from "../../../Helpers/socket";

export default function ChatWindow({ chatInfo, onStartChat, user }) {
  const navigate = useNavigate();
  const [showActionIcon, setShowActionIcon] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [senderId] = useState(user !== null ? user.data.id : "");
  const [newMessage, setNewMessage] = useState("");
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [popupToShow, setPopupToShow] = useState(null);
  const [toastList, setToastList] = useState([]);
  const [reportReason, setReportReason] = useState(null);
  const [preview, setPreview] = useState([]);
  const fileRef = useRef(null);
  const menuDropdownRef = useRef(null);
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [isMessageLoaded, setIsMessageLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isMessageSaved, setIsMessageSaved] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [loggedInUserProfile, setLoggedInUserProfile] = useState("");
  const textareaRef = useRef(null);
  const [isloaded, setIsLoaded] = React.useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoadedImg, setIsLoadedImg] = useState(false);
  const endOfMessagesRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState("below");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target) &&
        activeMenuId !== null
      ) {
        setActiveMenuId(null);
      }
    };

    if (activeMenuId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [activeMenuId]);

  const location = useLocation();
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userIdFromUrl = searchParams.get("userId");

    if (userIdFromUrl && chatUsers.length > 0) {
      const userFromUrl = chatUsers.find(
        (user) => String(user.user_id) === String(userIdFromUrl)
      );
      if (userFromUrl) {
        setSelectedUser(userFromUrl);
      } else {
        console.log("❌ ~ No matching user found for userId:", userIdFromUrl);
      }
    } else if (chatUsers.length > 0 && !selectedUser) {
      setSelectedUser(chatUsers[0]);
    }
  }, [chatUsers, location.search]);

  const handleUserChange = (item) => {
    setPreview([]);
    setIsInitialLoad(true);
    setSelectedUser(item);
    if (item?.user_id) {
      navigate(`/brand/chat?userId=${item.user_id}`, {
        replace: true,
      });
    }
  };

  useEffect(() => {
    async function getUsers() {
      try {
        const res = await chatService.getAllUsers();
        if (res.success) {
          let onlyUserToChat = res.data.filter(
            (item) => item.user_id !== user.data.id
          );
          let loggedInUserDetails =
            user.data.firstName + " " + user.data.lastName;
          setLoggedInUser(loggedInUserDetails);
          setChatUsers(onlyUserToChat);
          setLoggedInUserProfile(user.data.photo);

          const searchParams = new URLSearchParams(location.search);
          const userIdFromUrl = searchParams.get("userId");

          let initialSelectedUser = null;

          if (userIdFromUrl) {
            initialSelectedUser = onlyUserToChat.find(
              (user) => String(user.user_id) === String(userIdFromUrl)
            );
          }

          if (!initialSelectedUser && chatInfo && chatInfo.userId) {
            initialSelectedUser = onlyUserToChat.find(
              (user) => user.user_id === chatInfo.userId.id
            );
          }

          if (!initialSelectedUser && onlyUserToChat.length > 0) {
            initialSelectedUser = onlyUserToChat[0];
          }

          setSelectedUser(initialSelectedUser);
          setIsUserLoaded(true);
          setIsLoaded(true);
        }
      } catch (err) {
        if (err.apiResponseStatus !== 404) {
          let error = parseInput(err?.message);
          addToast({
            error: true,
            msg: error?.message,
            id: `${Date.now()}-${toastList.length}`,
          });
        } else {
          setIsLoaded(true);
        }
      }
    }

    getUsers();
  }, []);

  useEffect(() => {
    async function getMessages() {
      if (isInitialLoad || messages.length === 0) {
        setMessages([]);
        setIsMessageLoaded(true);
      }

      if (selectedUser?.user_id) {
        try {
          const res = await chatService.getMessages(
            selectedUser?.user_id,
            selectedUser?.type
          );
          if (res.success) {
            let allMessages = [];
            res.data.map((item) => {
              if (item.sender_id === user.data.id) {
                allMessages.push({
                  avatar: loggedInUserProfile,
                  id: item.id,
                  msg: item.message,
                  type: "sent",
                  name: loggedInUser,
                  messagetype: item.type,
                  visibility: item.visibility,
                  receiver_id: item.receiver_id,
                  sender_id: item.sender_id,
                  attachement: item.attachments,
                  flag_status: item.flag_status,
                  message_queue_id: item.message_queue_id,
                });
              } else {
                allMessages.push({
                  avatar: selectedUser.profileImg,
                  id: item.id,
                  msg: item.message,
                  type: "received",
                  name: loggedInUser,
                  messagetype: item.type,
                  visibility: item.visibility,
                  receiver_id: item.receiver_id,
                  sender_id: item.sender_id,
                  attachement: item.attachments,
                  flag_status: item.flag_status,
                  message_queue_id: item.message_queue_id,
                });
              }
            });
            setMessages(allMessages);
            setIsMessageLoaded(false);
            setIsInitialLoad(false);
            endOfMessagesRef.current?.scrollIntoView({ behavior: "instant" });
          }
        } catch (err) {
          let allMessages = [];
          setMessages(allMessages);
          setIsMessageLoaded(false);
          setIsInitialLoad(false);
          err.message &&
            addToast({
              error: true,
              msg:
                typeof err === "string"
                  ? err.replace(/<[^>]*>/g, "")
                  : err.message,
              id: `${Date.now()}-${toastList.length}`,
            });
        }
      }
    }
    getMessages();
  }, [selectedUser, isMessageSaved]);
  const saveMessage = async (param) => {
    try {
      const res = await chatService.saveMessage(selectedUser.user_id, param);
      if (res.success === true) {
      }
      setPopupToShow(null);
    } catch (err) {
      setPopupToShow(null);
      throw err;
    }
  };
  const decodeBase64Json = (encodedString) => {
    // Step 1: Decode the base64 string
    const decodedString = atob(encodedString); // This gives the raw JSON string
    // Step 2: Parse the JSON string to convert it back to a JavaScript object
    try {
      const jsonObject = JSON.parse(decodedString);
      return jsonObject;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return null; // Return null if there's an error
    }
  };
  const sendMessage = async () => {
    if (newMessage.trim() === "" && preview.length === 0) return;

    // Create optimistic message object
    const optimisticMessage = {
      avatar: loggedInUserProfile,
      id: `temp-${Date.now()}`,
      msg: newMessage.trim(),
      type: "sent",
      name: loggedInUser,
      messagetype: "user",
      visibility: "both",
      receiver_id: selectedUser.user_id,
      sender_id: senderId,
      attachement:
        preview.length > 0
          ? preview.map((p) => ({
              attachment_url: p.mediafile,
              attachment: p.file,
            }))
          : [],
      flag_status: null,
      message_queue_id: null,
    };

    // Add message to UI immediately
    setMessages((prevMessages) => [...prevMessages, optimisticMessage]);

    // Clear input and preview
    const messageToSend = newMessage.trim();
    const attachmentsToSend = [...preview];
    setNewMessage("");
    setPreview([]);

    try {
      if (attachmentsToSend.length > 0) {
        let param = {
          sender_id: senderId,
          message: messageToSend,
          receiver_type_id: selectedUser.type,
          attachments: attachmentsToSend,
        };
        await saveMessage(param);
      } else {
        let param = {
          sender_id: senderId,
          message: messageToSend,
          receiver_type_id: selectedUser.type,
          file: "",
        };
        await saveMessage(param);
      }
    } catch (err) {
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== optimisticMessage.id)
      );
      addToast({
        error: true,
        msg:
          typeof err === "string" ? err.replace(/<[^>]*>/g, "") : err.message,
        id: `${Date.now()}-${toastList.length}`,
      });
    }
  };
  const handleMenuClick = (e, id) => {
    e.stopPropagation();
    setActiveMenuId(id);
    // if (activeMenuId === id) {
    //   setActiveMenuId(null);
    //   return;
    // }
    const messageElement = e.currentTarget.closest(".message-header");
    if (messageElement) {
      const messageRect = messageElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - messageRect.bottom;
      const dropdownHeight = 100;
      if (spaceBelow < dropdownHeight) {
        setDropdownPosition("above");
      } else {
        setDropdownPosition("below");
      }
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const hidepopup = (e) => {
    // Removed - now handled by useEffect click-away listener
  };

  const confirmReportMessage = (e) => {
    e.stopPropagation();
    setPopupToShow("report");
    // setActiveMenuId(null);
  };

  const confirmSpam = (e) => {
    e.stopPropagation();
    setPopupToShow("spam");
    // setActiveMenuId(null);
  };

  const reportspam = async () => {
    try {
      const res = await chatService.reportspam(activeMenuId, {
        flag_type: "spam",
        flag_scope: "both",
      });
      if (res.success === true) {
        addToast({
          error: false,
          msg: res.message,
          id: `${Date.now()}-${toastList.length}`,
        });
        const filteredMessages = messages.filter(
          (msg) => msg.id !== activeMenuId
        );
        setMessages(filteredMessages);
      }
      setPopupToShow(null);
      setActiveMenuId(null);
    } catch (err) {
      setPopupToShow(null);
      addToast({
        error: true,
        msg:
          typeof err === "string" ? err.replace(/<[^>]*>/g, "") : err.message,
        id: `${Date.now()}-${toastList.length}`,
      });
    }
  };

  const reportMessage = async () => {
    try {
      const res = await chatService.reportMessage(activeMenuId, {
        report_reason: reportReason,
        flag_type: "reported",
        flag_scope: "both",
      });
      if (res.success === true) {
        addToast({
          error: false,
          msg: res.message,
          id: `${Date.now()}-${toastList.length}`,
        });
      }
      setPopupToShow(null);
      setActiveMenuId(null);
    } catch (err) {
      setPopupToShow(null);
      addToast({
        error: true,
        msg:
          typeof err === "string" ? err.replace(/<[^>]*>/g, "") : err.message,
        id: `${Date.now()}-${toastList.length}`,
      });
    }
  };
  const getFileNameFromUrl = (url) => {
    //const pathname = new URL(url).pathname;
    if (url) {
      return url.split("/").pop().split("?")[0];
    }
    return "";
  };
  const addToast = (data) => {
    const newToastList = [...toastList];
    newToastList.push({ ...data, id: `${Date.now()}-${toastList.length}` });
    setToastList(newToastList);
  };

  /*const downloadImg = async (media, filename) => {
    debugger;
    console.log(media);


    utils.downloadFile(media, 'download.jpg');
  }*/

  const downloadImg = async (media) => {
    const imageUrl = media;
    try {
      const link = document.createElement("a");
      link.href = imageUrl;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      // Clean up
      link.remove();
      // window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Check browser console for more info.");
    }
  };

  const previewImage = () => {
    setIsLoadedImg(false);
    const file = fileRef.current.files[0];

    if (file !== undefined) {
      const isValidType =
        file.type.startsWith("image/") ||
        file.type.startsWith("video/") ||
        file.type === "application/msword" || // .doc
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"; // .docx
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10 MB
      //if ((file.type !== "application/msword") && ((file.type.startsWith('image') === false && file.type.startsWith('video') === false) || (fileRef.current.files[0].size > 10000000))) {

      if (!isValidType || !isValidSize) {
        addToast({
          error: true,
          date: new Date(),
          msg: " .GIF, .PNG, .JPG, .MP4, .MOV,.Doc, .Docx only with a maximum file size of 10MB",
        });
        return;
      }

      const isBrand = user?.data?.active_role === 1;
      const isInfluencer = user?.data?.active_role === 3;

      console.log("🚀 ~ User Info:", {
        role_id: user?.data?.role_id,
        isBrand: isBrand,
        isInfluencer: isInfluencer,
        user: user?.data,
      });

      if (file) {
        try {
          const uploadFn = isBrand === false ? isBrand : isInfluencer;
          uploadService
            .uploadInfluencerChatAttachment(file, () => {}, 0, uploadFn)
            .then((text) => {
              console.log("🚀 ~ Upload raw response:", text);

              if (!text) {
                throw new Error("No response from upload service");
              }

              const decryptedResponse = text && decodeBase64Json(text);
              console.log("🚀 ~ Decrypted response:", decryptedResponse);

              if (!decryptedResponse) {
                throw new Error("Failed to decrypt upload response");
              }

              const response = JSON.parse(decryptedResponse);
              console.log("🚀 ~ Parsed upload response:", response);

              if (response.message === "success" && response.path) {
                setPreview((prevPreview) => [
                  ...prevPreview,
                  {
                    mediafile: response.path,
                    file_type: response.file_type,
                    file_size: response.file_size,
                    file: response.path,
                  },
                ]);
                setIsLoadedImg(true);

                console.log("🚀 ~ File added to preview:", {
                  path: response.path,
                  file_type: response.file_type,
                  file_size: response.file_size,
                });
              } else {
                console.error("🚀 ~ Upload failed:", response);
                addToast({
                  error: true,
                  date: new Date(),
                  msg: response.message || "Upload failed - no path returned",
                });
              }
            })
            .catch((err) => {
              console.error("🚀 ~ Upload promise error:", err);
              addToast({
                error: true,
                date: new Date(),
                msg: "Error uploading file: " + (err.message || err),
              });
            });
        } catch (err) {
          console.error("🚀 ~ Upload exception:", err);
          addToast({
            error: true,
            date: new Date(),
            msg: "Error uploading file",
          });
        }
      }
    }
  };
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview, preview]);

  const gotoDisscovery = () => {
    navigate("/brand/influencerdiscovery");
  };
  const removeImgFromArray = (index) => {
    const temp = [...preview];
    // removing the element using splice
    temp.splice(index, 1);
    setPreview(temp);
  };

  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const maxHeight = 12 * 10;
      textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + "px";
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    autoResizeTextarea();
  };

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: "instant" });
  }, [messages, preview]);

  const [socketData, setSocketData] = useState(null);
  console.log("🚀 ~ ChatWindow ~ socketData:", socketData);

  // Socket connection management
  useEffect(() => {
    // Connect socket when component mounts
    socketService.connect();
    console.log("Socket connection established in ChatWindow");

    // Listen for socket messages
    socketService.on("message", (event) => {
      try {
        const parsedMessage = JSON.parse(event.data);
        const formattedData = {
          event: parsedMessage.event,
          data: JSON.parse(parsedMessage.data),
        };
        console.log("🚀 ~ ChatWindow ~ formattedData:", formattedData.data);
        if (formattedData.event === "MessageReceived") {
          if (formattedData.data.sender_id !== user.data.id) {
            setIsMessageSaved((prev) => !prev);
          }
        }
        setSocketData(formattedData);

        if (formattedData.event === "pusher:connection_established") {
          setSocketData(formattedData);
        }
      } catch (error) {
        console.error("Error parsing socket message:", error);
      }
    });

    // Cleanup function - disconnect when component unmounts or page changes
    return () => {
      socketService.off("message");
      socketService.disconnect();
      console.log("Socket disconnected from ChatWindow");
    };
  }, [user, selectedUser, loggedInUserProfile, loggedInUser]);

  // Authenticate and subscribe to socket channel after messages are loaded
  useEffect(() => {
    const authenticateAndSubscribe = async () => {
      if (
        socketData?.event === "pusher:connection_established" &&
        messages?.length > 0
      ) {
        try {
          const channelName = `private-conversation.${selectedUser?.message_queue_id}`;
          const result = await socketAuthService.authenticateSocket(
            socketData.data.socket_id,
            channelName
          );
          console.log("🚀 ~ authenticateSocket ~ result:", result.auth);

          if (result.auth) {
            const subscribeData = {
              event: "pusher:subscribe",
              data: {
                auth: result.auth,
                channel: channelName,
              },
            };
            socketService.send(subscribeData);
            console.log("Subscribing to channel:", channelName);
          }
        } catch (error) {
          console.error("Socket authentication failed:", error);
        }
      }
    };

    authenticateAndSubscribe();
  }, [messages, socketData]);

  // Handle page visibility change and beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      socketService.disconnect();
      console.log("Socket disconnected - page closing");
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        socketService.disconnect();
        console.log("Socket disconnected - page hidden");
      } else {
        socketService.connect();
        console.log("Socket reconnected - page visible");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // WhatsApp-style Message Loader Component
  const MessageLoader = () => (
    <div className="flex flex-col gap-4 p-4">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className={`flex ${item === 2 ? "justify-end" : "justify-start"}`}
        >
          <div
            className={`flex flex-col gap-2 ${
              item === 2 ? "items-end" : "items-start"
            }`}
          >
            <div className="flex gap-2 items-center">
              {item !== 2 && (
                <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
              )}
              <div
                className={`h-4 ${
                  item === 2 ? "w-24" : "w-32"
                } bg-gray-200 rounded animate-pulse`}
              />
            </div>
            <div
              className={`rounded-2xl p-4 ${
                item === 2
                  ? "bg-green-100 rounded-tr-none"
                  : "bg-gray-100 rounded-tl-none"
              } animate-pulse`}
              style={{
                width: item === 1 ? "280px" : item === 2 ? "220px" : "300px",
              }}
            >
              <div className="space-y-2">
                <div className="h-3 bg-gray-300 rounded w-full" />
                <div className="h-3 bg-gray-300 rounded w-4/5" />
                <div className="h-3 bg-gray-300 rounded w-3/5" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {chatUsers?.length > 0 ? (
        <>
          <div className="chat-user-select">
            {isUserLoaded && chatUsers?.length > 0 && (
              <SelectInputWithImg
                list={chatUsers}
                selected={selectedUser}
                onChange={handleUserChange}
                value={selectedUser}
              />
            )}
          </div>
          <div className="chat-container border-gray-200">
            <>
              <div
                className="chat-messages bg-white border-b"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#e5e7eb transparent",
                }}
              >
                {isMessageLoaded ? (
                  <MessageLoader />
                ) : (
                  <>
                    {messages?.length > 0 ? (
                      messages?.map(
                        (msg) =>
                          (msg.messagetype === "user" ||
                            (msg.messagetype === "system" &&
                              msg.visibility === "both") ||
                            (msg.messagetype === "system" &&
                              msg.visibility === "sender" &&
                              senderId === msg.sender_id) ||
                            (msg.messagetype === "system" &&
                              msg.visibility === "receiver" &&
                              user.data.id === msg.receiver_id)) && (
                            <div
                              key={msg.id}
                              className={`flex ${
                                msg.type === "sent"
                                  ? "justify-end"
                                  : "justify-start"
                              } ${
                                msg.messagetype === "user" ? msg.type : "system"
                              }`}
                              onClick={hidepopup}
                            >
                              <div
                                className={`message-header !flex rounded-[32px] px-6 py-4 text-sm max-w-[80%] relative ${
                                  msg.messagetype === "user" &&
                                  msg.type === "sent"
                                    ? "bg-green-300 text-gray-800 rounded-tr-none"
                                    : msg.isLoading
                                    ? "bg-gray-200 animate-pulse rounded-tl-none"
                                    : "bg-gray-100 text-gray-800 rounded-tl-none"
                                }`}
                              >
                                {msg.attachement &&
                                msg.attachement.length > 0 ? (
                                  <div className="mr-2">
                                    <div className="!flex justify-between">
                                      <div className="message-text ml-0 text-md ">
                                        {msg.msg}
                                      </div>
                                      <div>
                                        {msg.type !== "sent" &&
                                          msg.flag_status !== "reported" && (
                                            <span
                                              className=" cursor-pointer absolute "
                                              onClick={(e) =>
                                                handleMenuClick(e, msg.id)
                                              }
                                            >
                                              <ChevronDown
                                                size={20}
                                                color="#9a9393"
                                              />
                                            </span>
                                          )}
                                      </div>
                                    </div>

                                    <div className="message-attachments mt-4">
                                      {msg.attachement.map(
                                        (attachmentimg, idx) => {
                                          return (
                                            <div
                                              key={idx}
                                              className="file-attachment-item flex gap-2 mb-2 last:mb-0 p-2 bg-white bg-opacity-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-opacity-70 transition-colors"
                                              onClick={() =>
                                                downloadImg(
                                                  attachmentimg.attachment_url
                                                )
                                              }
                                            >
                                              {downloadImgInChat}{" "}
                                              {getFileNameFromUrl(
                                                attachmentimg.attachment
                                              )}
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex justify-between gap-2 mr-2">
                                      <div className="message-text ml-0 text-md">
                                        {msg.msg}
                                      </div>
                                      <div>
                                        {msg.type !== "sent" &&
                                          msg.flag_status !== "reported" && (
                                            <span
                                              className="menu-dots cursor-pointer absolute "
                                              onClick={(e) =>
                                                handleMenuClick(e, msg.id)
                                              }
                                            >
                                              <ChevronDown
                                                size={20}
                                                color="#9a9393"
                                              />
                                            </span>
                                          )}
                                      </div>
                                    </div>
                                  </>
                                )}
                                {(msg.flag_status === "reported" ||
                                  msg?.flag_status?.toLowerCase() ===
                                    "spam") && (
                                  <Tooltip
                                    title={
                                      "This message was reported or marked as spam"
                                    }
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "rgba(97, 97, 97, 0.95)",
                                          color: "white",
                                          fontSize: "12px",
                                          maxWidth: "250px",
                                          padding: "8px 12px",
                                          borderRadius: "6px",
                                          boxShadow:
                                            "0 4px 14px rgba(0, 0, 0, 0.15)",
                                        },
                                      },
                                      arrow: {
                                        sx: {
                                          color: "rgba(97, 97, 97, 0.95)",
                                        },
                                      },
                                    }}
                                  >
                                    <CircleAlert size={20} color="red" />
                                  </Tooltip>
                                )}
                                {activeMenuId === msg.id && (
                                  <div
                                    ref={menuDropdownRef}
                                    className="absolute left-full top-5 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[180px] z-50"
                                  >
                                    <div
                                      onClick={confirmReportMessage}
                                      className="flex items-center gap-2 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors first:rounded-t-lg border-b border-gray-100"
                                    >
                                      {reportError}
                                      <span className="text-sm text-gray-700">
                                        Report Message
                                      </span>
                                    </div>
                                    <div
                                      onClick={confirmSpam}
                                      className="flex items-center gap-2 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors last:rounded-b-lg"
                                    >
                                      {reportSpam}
                                      <span className="text-sm text-gray-700">
                                        Report as Spam
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                      )
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                        No messages yet. Start the conversation!
                      </div>
                    )}
                  </>
                )}
                <div ref={endOfMessagesRef} />
              </div>

              <div className="chat-input-image">
                {preview.length > 0 && (
                  <div className="ShowPreview">
                    {preview.map((file, index) => {
                      return (
                        <div key={index}>
                          <div
                            onClick={() => {
                              removeImgFromArray(index);
                            }}
                            className="trashIconForImgUpload"
                          >
                            {trashGalleryIcon}
                          </div>
                          {file.file_type !== "mp4" &&
                            file.file_type !== "mov" &&
                            file.file_type !== "doc" &&
                            file.file_type !== "docx" && (
                              <img src={file.mediafile} alt="" />
                            )}
                          {file.file_type === "mp4" && (
                            <>
                              <GalleryVideo
                                setShowActionIcon={setShowActionIcon}
                                key={file.mediafile}
                                media_url={file.mediafile}
                                status={"visible"}
                                media_type_id={1}
                              />
                            </>
                          )}
                          {(file.file_type === "doc" ||
                            file.file_type === "docx") && <>{documentIcon}</>}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center gap-3 bg-white border border-border focus-within:ring-2 focus-within:ring-primary rounded-xl !px-4 !py-2 m-5">
                  {/* Text Input */}
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Message"
                    className="flex-1 resize-none bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground leading-[1.5rem] max-h-[120px]"
                    rows={1}
                  />

                  {/* upload file */}
                  <label
                    htmlFor="file-upload"
                    className="file-upload-label p-2 bg-[#044433] hover:bg-[#102620]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors text-primary-foreground  "
                  >
                    <Paperclip
                      size={20}
                      color="#ffffff"
                      className="h-4 w-4 text-white"
                    />
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    ref={fileRef}
                    onChange={previewImage} // Handle upload logic
                    style={{ display: "none" }}
                  />

                  {/* Send Button */}
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() && preview.length === 0}
                    className="p-2 bg-[#044433] hover:bg-[#102620]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors text-primary-foreground"
                  >
                    <Send className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>
            </>
            {toastList && <Toast list={toastList} setList={setToastList} />}
          </div>
          {isloaded && popupToShow === "report" && (
            <ModalConfirmReportMessage
              close={() => {
                setActiveMenuId(null);
                setPopupToShow(null);
                setReportReason(null);
              }}
              reportMessage={reportMessage}
              setReportReason={setReportReason}
            />
          )}
          {isloaded && popupToShow === "spam" && (
            <ModalConfirmSpam
              close={() => {
                setActiveMenuId(null);
                setPopupToShow(null);
              }}
              reportspam={reportspam}
            />
          )}
        </>
      ) : (
        !chatUsers.length && (
          <div>
            <h6 className="center-align">
              No Collaboration Requests From Brands Yet. Check New{" "}
              <span onClick={gotoDisscovery}>{redirecttoPage}</span>
            </h6>
          </div>
        )
      )}
      {toastList.length > 0 && (
        <Toast type="rate" list={toastList} setList={setToastList} />
      )}
    </div>
  );
}
